import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../Auth/AuthContext';
import { 
  ArrowLeft, 
  LayoutGrid, 
  List, 
  Heart, 
  MapPin, 
  Eye, 
  Navigation,
  ChevronLeft,
  ChevronRight,
  Clock,
  Users,
  Camera,
  Sun,
  Cloud,
  Accessibility
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  WEATHER_OPTIONS, 
  LIGHT_OPTIONS, 
  CROWD_LEVEL_OPTIONS, 
  TIME_WINDOW_OPTIONS,
  DIFFICULTY_OPTIONS,
  getLabel, 
  getIcon 
} from '../../constants/spotEnums';

function Gallery() {
  const { state, actions } = useApp();
  const { user: authUser, token } = useAuth(); // Use AuthContext for authentication
  const navigate = useNavigate();
  
  // Local states for gallery functionality - exactly like old App.js
  const [tikTokGalleryMode, setTikTokGalleryMode] = useState('scroll'); // 'scroll' or 'grid'
  const [tikTokCurrentIndex, setTikTokCurrentIndex] = useState(0);
  const [allPhotosWithSpots, setAllPhotosWithSpots] = useState([]);
  const [preserveCurrentPhoto, setPreserveCurrentPhoto] = useState(false);

  // Fetch liked photos when gallery opens if user is logged in
  useEffect(() => {
    if (state.showTikTokGallery && authUser) {
      actions.fetchLikedPhotos();
    }
  }, [state.showTikTokGallery, authUser]);

  // Don't render if gallery not shown
  if (!state.showTikTokGallery) {
    return null;
  }

  // Create array of photos based on mode (all spots or specific spot)
  useEffect(() => {
    if (state.spots.length > 0) {
      // Store current photo info if we need to preserve it
      const currentPhoto = preserveCurrentPhoto && allPhotosWithSpots[tikTokCurrentIndex] ? 
        allPhotosWithSpots[tikTokCurrentIndex] : null;
      
      const allPhotos = [];
      
      if (state.tikTokSpotSpecificMode && state.tikTokTargetSpotId) {
        // Spot-specific mode: only photos from the target spot
        const targetSpot = state.spots.find(spot => spot.id === state.tikTokTargetSpotId);
        if (targetSpot && targetSpot.photos && targetSpot.photos.length > 0) {
          targetSpot.photos.forEach((photo, photoIndex) => {
            allPhotos.push({
              url: typeof photo === 'string' ? photo : photo.url,
              metadata: typeof photo === 'object' ? photo.metadata : null,
              spot: targetSpot,
              photoIndex: photoIndex
            });
          });
        }
      } else {
        // Normal mode: all photos from all spots
        state.spots.forEach(spot => {
          if (spot.photos && spot.photos.length > 0) {
            spot.photos.forEach((photo, photoIndex) => {
              allPhotos.push({
                url: typeof photo === 'string' ? photo : photo.url,
                metadata: typeof photo === 'object' ? photo.metadata : null,
                spot: spot,
                photoIndex: photoIndex
              });
            });
          }
        });
        
        // Only shuffle on initial load, not when updating existing photos
        if (allPhotosWithSpots.length === 0) {
          // First load - shuffle randomly
          allPhotos.sort(() => Math.random() - 0.5);
        }
        // If allPhotosWithSpots already exists, don't shuffle to preserve current order
      }
      
      setAllPhotosWithSpots(allPhotos);
      
      // If we need to preserve the current photo, find it in the new array and set the index
      if (preserveCurrentPhoto && currentPhoto) {
        const newIndex = allPhotos.findIndex(photo => 
          photo.spot.id === currentPhoto.spot.id && 
          photo.photoIndex === currentPhoto.photoIndex
        );
        
        if (newIndex !== -1 && newIndex !== tikTokCurrentIndex) {
          setTikTokCurrentIndex(newIndex);
        }
        
        // Reset the preserve flag
        setPreserveCurrentPhoto(false);
      }
    }
  }, [state.spots, state.tikTokSpotSpecificMode, state.tikTokTargetSpotId, preserveCurrentPhoto]);

  // Keyboard navigation - exactly like old App.js
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!state.showTikTokGallery) return;
      
      switch (e.key) {
        case 'Escape':
          closeTikTokGallery();
          break;
        case 'ArrowLeft':
          navigateTikTokGallery('prev');
          break;
        case 'ArrowRight':
          navigateTikTokGallery('next');
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateTikTokGallery('prev');
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigateTikTokGallery('next');
          break;
        default:
          break;
      }
    };

    if (state.showTikTokGallery) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [state.showTikTokGallery, tikTokCurrentIndex, allPhotosWithSpots]);

  // Navigation functions - exactly like old App.js
  const navigateTikTokGallery = (direction) => {
    if (allPhotosWithSpots.length === 0) return;
    
    if (direction === 'next') {
      setTikTokCurrentIndex((tikTokCurrentIndex + 1) % allPhotosWithSpots.length);
    } else {
      setTikTokCurrentIndex((tikTokCurrentIndex - 1 + allPhotosWithSpots.length) % allPhotosWithSpots.length);
    }
  };

  const navigateToTikTokPhoto = (index) => {
    setTikTokCurrentIndex(index);
    setTikTokGalleryMode('scroll');
  };

  const toggleTikTokGalleryMode = () => {
    setTikTokGalleryMode(tikTokGalleryMode === 'scroll' ? 'grid' : 'scroll');
  };

  const closeTikTokGallery = () => {
    // Reset spot-specific mode when closing
    if (state.tikTokSpotSpecificMode) {
      actions.setTikTokSpotSpecificMode(false);
      actions.setTikTokTargetSpotId(null);
    }
    actions.setShowTikTokGallery(false);
  };

  // Helper functions - exactly like old App.js
  const getDistanceFromUser = (spot) => {
    // Use pre-calculated distance if available
    if (spot.distanceFormatted !== undefined) {
      return spot.distanceFormatted;
    }

    // Fallback to manual calculation
    if (state.userLocation && spot.latitude && spot.longitude) {
      const R = 6371;
      const dLat = (spot.latitude - state.userLocation[0]) * Math.PI / 180;
      const dLon = (spot.longitude - state.userLocation[1]) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(state.userLocation[0] * Math.PI / 180) * Math.cos(spot.latitude * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      return Math.round(distance * 10) / 10 + 'km';
    }
    return null;
  };

  const getOccupationColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'faible':
        return 'text-green-500';
      case 'modérée':
        return 'text-yellow-500';
      case 'élevée':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const showSpotOnMap = (spot) => {
    // Fermer la galerie
    actions.setShowTikTokGallery(false);
    
    // Sélectionner le spot
    actions.setSelectedSpot(spot);
    actions.setSelectedSpotFromPin(spot);
    
    // Ouvrir les détails du spot pour que l'utilisateur voie la fiche
    actions.setShowSpotDetails(true);
    
    // Centrer la carte sur le spot après un délai pour laisser le temps à la page de se charger
    setTimeout(() => {
      // Essayer d'accéder à la carte via différentes méthodes
      if (state.mapRef && state.mapRef.setView) {
        state.mapRef.setView([spot.latitude, spot.longitude], 15);
      } else if (window.mapInstance) {
        window.mapInstance.setView([spot.latitude, spot.longitude], 15);
      } else {
        // Fallback: déclencher un événement personnalisé que la carte peut écouter
        const event = new CustomEvent('centerMapOnSpot', {
          detail: { latitude: spot.latitude, longitude: spot.longitude, zoom: 15 }
        });
        window.dispatchEvent(event);
      }
    }, 500);
  };

  const showSpotDetailsFromGallery = (spot) => {
    // Sélectionner le spot
    actions.setSelectedSpot(spot);
    
    // Fermer la galerie d'abord
    actions.setShowTikTokGallery(false);
    
    // Ouvrir la modal de détails après un petit délai
    setTimeout(() => {
      actions.setShowSpotDetails(true);
    }, 100);
  };

  const handleTikTokSpotLike = async (spot) => {
    const isLiked = state.likedSpots.has(spot.id);
    const action = isLiked ? 'dislike' : 'like';
    
    // Mark that we want to preserve current photo during the update
    setPreserveCurrentPhoto(true);
    
    try {
      await actions.likeSpot(spot.id, action);
    } catch (error) {
      console.error('Error liking spot:', error);
    }
  };

  const handlePhotoLike = async (photoUrl, spotId) => {
    console.log('handlePhotoLike called');
    console.log('authUser:', authUser);
    console.log('token:', token ? 'present' : 'missing');
    
    if (!authUser) {
      // User not logged in, redirect to login
      console.log('User not authenticated, redirecting to login');
      actions.setShowTikTokGallery(false);
      navigate('/login');
      return;
    }
    
    try {
      console.log('Attempting to like photo:', photoUrl, 'for spot:', spotId);
      await actions.likePhoto(photoUrl, spotId);
      console.log('Photo like successful');
    } catch (error) {
      console.error('Error liking photo:', error);
      // Don't re-throw - just log the error
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-[10001] flex overflow-hidden tiktok-gallery-overlay">
      {/* Photo Section - Dynamic based on mode */}
      <div className="flex-1 relative flex items-center justify-center">
        {allPhotosWithSpots.length > 0 && (
          <>
            {tikTokGalleryMode === 'scroll' ? (
              // Scroll Mode - Single Photo Full Screen
              <>
                <div className="relative w-full h-full flex items-center justify-center tiktok-photo-container">
                  <img
                    src={allPhotosWithSpots[tikTokCurrentIndex]?.url}
                    alt={`Photo de ${allPhotosWithSpots[tikTokCurrentIndex]?.spot?.title}`}
                    className="max-w-full max-h-full object-contain"
                    style={{ userSelect: 'none' }}
                  />
                  
                  {/* Photo Like Button - Bottom Right Corner */}
                  <button
                    onClick={() => handlePhotoLike(
                      allPhotosWithSpots[tikTokCurrentIndex]?.url,
                      allPhotosWithSpots[tikTokCurrentIndex]?.spot?.id
                    )}
                    className={`absolute bottom-4 right-4 rounded-full p-3 transition-all shadow-lg ${
                      state.likedPhotos.has(allPhotosWithSpots[tikTokCurrentIndex]?.url)
                        ? 'bg-pink-500 hover:bg-pink-600 text-white'
                        : 'bg-black/50 hover:bg-black/70 text-white'
                    }`}
                    title={state.likedPhotos.has(allPhotosWithSpots[tikTokCurrentIndex]?.url) ? 'Photo likée' : 'Liker cette photo'}
                  >
                    <Heart 
                      className={`w-6 h-6 ${state.likedPhotos.has(allPhotosWithSpots[tikTokCurrentIndex]?.url) ? 'fill-white' : ''}`} 
                    />
                  </button>
                  
                  {/* Photo Navigation Arrows */}
                  <button
                    onClick={() => navigateTikTokGallery('prev')}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-3 hover:bg-black/70 tiktok-nav-button"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={() => navigateTikTokGallery('next')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-3 hover:bg-black/70 tiktok-nav-button"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
              </>
            ) : (
              // Grid Mode - Multiple Photos
              <div className="w-full h-full overflow-y-auto p-4 tiktok-grid-container">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                  {allPhotosWithSpots.map((photoData, index) => (
                    <div
                      key={index}
                      className="relative aspect-square cursor-pointer group tiktok-grid-item"
                      onClick={() => navigateToTikTokPhoto(index)}
                    >
                      <img
                        src={photoData.url}
                        alt={`Photo de ${photoData.spot?.title}`}
                        className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                      />
                      
                      {/* Spot title overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity tiktok-grid-overlay">
                        <p className="text-sm font-medium truncate">
                          {photoData.spot?.title}
                        </p>
                        <p className="text-xs text-gray-300 truncate">
                          {photoData.spot?.author}
                        </p>
                      </div>
                      
                      {/* Selected indicator */}
                      {index === tikTokCurrentIndex && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1 tiktok-selected-indicator">
                          <Eye className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Right Panel - Spot Information */}
      <div className="w-80 bg-white p-6 overflow-y-auto tiktok-info-panel">
        {allPhotosWithSpots.length > 0 && (
          <>
            {/* Header with mode toggle and close button */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-bold">Galerie</h2>
                <Button
                  onClick={toggleTikTokGalleryMode}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-1"
                >
                  {tikTokGalleryMode === 'scroll' ? (
                    <>
                      <LayoutGrid className="w-4 h-4" />
                      <span>Grille</span>
                    </>
                  ) : (
                    <>
                      <List className="w-4 h-4" />
                      <span>Scroll</span>
                    </>
                  )}
                </Button>
              </div>
              <Button
                onClick={closeTikTokGallery}
                variant="outline"
                size="sm" 
                className="flex items-center space-x-1"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Retour</span>
              </Button>
            </div>

            {/* Current Spot Information */}
            {(() => {
              const currentPhoto = allPhotosWithSpots[tikTokCurrentIndex];
              const currentSpot = currentPhoto?.spot;
              if (!currentSpot) return null;

              const distance = getDistanceFromUser(currentSpot);
              const isLiked = state.likedSpots.has(currentSpot.id);
              const isPhotoLiked = state.likedPhotos.has(currentPhoto?.url);

              return (
                <div className="space-y-6">
                  {/* Photo Like Button */}
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-100">
                    <div className="flex items-center space-x-2">
                      <Camera className="w-5 h-5 text-pink-500" />
                      <span className="text-sm font-medium text-gray-700">Cette photo</span>
                    </div>
                    <Button
                      onClick={() => handlePhotoLike(currentPhoto?.url, currentSpot.id)}
                      variant={isPhotoLiked ? "default" : "outline"}
                      size="sm"
                      className={`flex items-center space-x-1 ${
                        isPhotoLiked 
                          ? 'bg-pink-500 hover:bg-pink-600 text-white border-pink-500' 
                          : 'border-pink-300 text-pink-600 hover:bg-pink-50'
                      }`}
                    >
                      <Heart className={`w-4 h-4 ${isPhotoLiked ? 'fill-white' : ''}`} />
                      <span>{isPhotoLiked ? 'Photo likée' : 'Liker'}</span>
                    </Button>
                  </div>

                  {/* Spot Title & Author */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {currentSpot.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Par {currentSpot.author}
                    </p>
                    {distance && (
                      <div className="mt-2">
                        <p className="text-sm text-blue-600 font-medium">
                          📍 À {distance} de vous
                        </p>
                        <Button
                          onClick={() => showSpotOnMap(currentSpot)}
                          variant="outline"
                          size="sm"
                          className="mt-2 flex items-center space-x-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <MapPin className="w-4 h-4" />
                          <span>Afficher sur la carte</span>
                        </Button>
                      </div>
                    )}
                    {!distance && (
                      <div className="mt-2">
                        <Button
                          onClick={() => showSpotOnMap(currentSpot)}
                          variant="outline"
                          size="sm"
                          className="mt-2 flex items-center space-x-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <MapPin className="w-4 h-4" />
                          <span>Afficher sur la carte</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {currentSpot.description}
                    </p>
                  </div>

                  {/* Tags */}
                  {currentSpot.tags && currentSpot.tags.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {currentSpot.tags.map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* V2 Metadata - Créneaux recommandés */}
                  {(() => {
                    // Support both v2_data wrapper and direct spot properties
                    const timeWindows = currentSpot.v2_data?.recommended_time_windows || currentSpot.recommended_time_windows;
                    if (timeWindows && timeWindows.length > 0) {
                      return (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Clock className="w-4 h-4 mr-1 text-amber-500" />
                            Créneaux recommandés
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {timeWindows.map((tw, index) => {
                              // tw can be a string or an object with {type, start_time_local, end_time_local, notes}
                              const timeType = typeof tw === 'string' ? tw : tw.type;
                              return (
                                <Badge key={index} variant="secondary" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  {getIcon(TIME_WINDOW_OPTIONS, timeType)} {getLabel(TIME_WINDOW_OPTIONS, timeType)}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* V2 Metadata - Conditions idéales */}
                  {(() => {
                    const idealConditions = currentSpot.v2_data?.ideal_conditions || currentSpot.ideal_conditions;
                    if (idealConditions) {
                      return (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Sun className="w-4 h-4 mr-1 text-yellow-500" />
                            Conditions idéales
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {/* Météo */}
                            {idealConditions.weather?.map((w, index) => (
                              <Badge key={`weather-${index}`} variant="secondary" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {getIcon(WEATHER_OPTIONS, w)} {getLabel(WEATHER_OPTIONS, w)}
                              </Badge>
                            ))}
                            {/* Lumière */}
                            {idealConditions.light?.map((l, index) => (
                              <Badge key={`light-${index}`} variant="secondary" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                                {getIcon(LIGHT_OPTIONS, l)} {getLabel(LIGHT_OPTIONS, l)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* V2 Metadata - Affluence */}
                  {(() => {
                    const crowdLevel = currentSpot.v2_data?.crowd_level || currentSpot.crowd_level;
                    if (crowdLevel) {
                      return (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Users className="w-4 h-4 mr-1 text-purple-500" />
                            Affluence
                          </h4>
                          <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            {getIcon(CROWD_LEVEL_OPTIONS, crowdLevel)} {getLabel(CROWD_LEVEL_OPTIONS, crowdLevel)}
                          </Badge>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* V2 Metadata - Accessibilité */}
                  {(() => {
                    const accessibility = currentSpot.v2_data?.accessibility || currentSpot.accessibility;
                    if (accessibility) {
                      return (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Accessibility className="w-4 h-4 mr-1 text-green-500" />
                            Accessibilité
                          </h4>
                          <div className="space-y-1 text-sm text-gray-600">
                            {accessibility.difficulty && (
                              <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200 mr-2">
                                {getIcon(DIFFICULTY_OPTIONS, accessibility.difficulty)} {getLabel(DIFFICULTY_OPTIONS, accessibility.difficulty)}
                              </Badge>
                            )}
                            {accessibility.walking_time_minutes && (
                              <span className="text-xs text-gray-500">
                                🚶 {accessibility.walking_time_minutes} min de marche
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Legacy Practical Info - Fallback for old data without v2 */}
                  {!currentSpot.v2_data && !currentSpot.recommended_time_windows && currentSpot.practical_info && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Infos pratiques</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        {currentSpot.practical_info.recommended_hours && (
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-blue-500" />
                            <span>{currentSpot.practical_info.recommended_hours}</span>
                          </div>
                        )}
                        {currentSpot.practical_info.accessibility && (
                          <div className="flex items-center space-x-2">
                            <Navigation className="w-4 h-4 text-purple-500" />
                            <span>{currentSpot.practical_info.accessibility}</span>
                          </div>
                        )}
                        {currentSpot.practical_info.occupation_level && (
                          <div className="flex items-center space-x-2">
                            <Users className={`w-4 h-4 ${getOccupationColor(currentSpot.practical_info.occupation_level)}`} />
                            <span className={getOccupationColor(currentSpot.practical_info.occupation_level)}>
                              {currentSpot.practical_info.occupation_level}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="pt-4 border-t border-gray-200 space-y-3">
                    <Button
                      onClick={() => showSpotDetailsFromGallery(currentSpot)}
                      variant="outline"
                      className="w-full py-2 text-sm font-medium border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Plus de détails
                    </Button>
                    
                    <Button
                      onClick={() => handleTikTokSpotLike(currentSpot)}
                      className={`w-full py-3 text-base font-medium transition-all ${
                        isLiked 
                          ? 'bg-red-500 hover:bg-red-600 text-white' 
                          : 'bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-600 border border-gray-300'
                      }`}
                    >
                      <Heart className={`w-5 h-5 mr-2 ${isLiked ? 'fill-white text-white' : 'text-gray-400'}`} />
                      {isLiked ? 'Spot liké ❤️' : 'Liker ce spot'}
                    </Button>

                    <div className="text-center text-sm text-gray-500 mt-2">
                      <Heart className="w-4 h-4 inline mr-1 text-red-400" />
                      {currentSpot.likes || 0} likes
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}

export default Gallery;